import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import { CircularProgress } from "@mui/material";

type ActionVisibility = "always" | "onInput";
type ErrorDisplay = "text" | "tooltip";
interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  searchBarRef?: React.MutableRefObject<HTMLDivElement | null>;
  onClose?: () => void;
  onFocus?: () => void;
  onClick?: () => void;
  onSubmit?: () => void;
  error?: string | null;
  errorDisplay?: ErrorDisplay;
  disabled?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  leadingIcon?: "search" | "link";
  isLoading?: boolean;
  actionIcon?: React.ReactNode;
  actionTooltip?: string;
  actionVisibility?: ActionVisibility;
  submitVisibility?: ActionVisibility;
  onAction?: (value: string) => void;
  /** Runs before the internal Enter→onSubmit handling; preventDefault to override it. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Extra attributes spread onto the `<input>` (e.g. combobox ARIA wiring). */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & {
    ref?: React.Ref<HTMLInputElement>;
  };
  /** Control sizing: "sm" (32px, default) or "lg" (44px hero, large type). */
  size?: "sm" | "lg";
  /** The list pages' borderless look at the small height (`lg` has it already). */
  quiet?: boolean;
  /** Rendered in the right adornment slot (e.g. a ⌘K hint chip). */
  trailingAdornment?: React.ReactNode;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = "Search",
  className = "",
  searchBarRef,
  onClose,
  onFocus,
  onClick,
  onSubmit,
  error,
  errorDisplay = "text",
  disabled = false,
  onPaste,
  leadingIcon = "search",
  isLoading = false,
  actionIcon,
  actionTooltip,
  actionVisibility = "onInput",
  submitVisibility = "always",
  onAction,
  onKeyDown,
  inputProps,
  size = "sm",
  quiet = false,
  trailingAdornment,
}) => {
  const trimmedValue = value.trim();
  // The trailing slot is absolutely positioned, so the input must reserve its
  // width or typed text runs underneath a wide adornment.
  const trailingRef = useRef<HTMLDivElement | null>(null);
  const [trailingWidth, setTrailingWidth] = useState(0);
  const showSubmitButton =
    Boolean(onSubmit) &&
    (submitVisibility === "always" || trimmedValue.length > 0);
  const shouldShowActionButton =
    actionVisibility === "always"
      ? true
      : trimmedValue.length > 0 && (Boolean(onAction || onClose) || !onSubmit);

  const handleActionClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    if (onAction) {
      onAction(value);
      return;
    }
    if (onClose) {
      onClose();
      return;
    }
    onChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === "Enter" && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
    // Not stopped: a dialog or picker around the field still closes on it.
    if (e.key === "Escape" && value) {
      onChange("");
      e.currentTarget.blur();
    }
  };

  const isTooltipError = errorDisplay === "tooltip" && !!error;

  useLayoutEffect(() => {
    setTrailingWidth(trailingRef.current?.offsetWidth ?? 0);
  }, [trailingAdornment, shouldShowActionButton, showSubmitButton]);

  return (
    <div className={`w-full ${className}`}>
      <div
        ref={searchBarRef}
        className="w-full relative group/searchbar"
        onClick={onClick}
      >
        <div
          className={`absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none ${
            size === "lg" || quiet
              ? "text-fg-muted group-hover/searchbar:text-fg group-focus-within/searchbar:text-accent"
              : "text-fg-muted"
          }`}
        >
          {leadingIcon === "link" ? (
            <LinkIcon
              sx={{
                fontSize: 16,
                color: "inherit",
              }}
            />
          ) : (
            <SearchIcon
              sx={{
                fontSize: 16,
                color: "inherit",
              }}
            />
          )}
        </div>
        <Tooltip
          title={isTooltipError ? error : ""}
          open={isTooltipError}
          placement="bottom"
        >
          <input
            type="text"
            {...inputProps}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={onPaste}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-invalid={!!error}
            className={`w-full ${size === "lg" ? "input-custom-lg" : quiet ? "input-custom-quiet-sm" : "input-custom-sm"} pl-8 pr-8`}
            style={{
              ...inputProps?.style,
              paddingRight: trailingWidth
                ? `calc(${trailingWidth}px + var(--spacing-lg))`
                : undefined,
            }}
          />
        </Tooltip>
        {(shouldShowActionButton || showSubmitButton || trailingAdornment) && (
          <div
            ref={trailingRef}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1"
          >
            {trailingAdornment}
            {shouldShowActionButton && (
              <div
                className="cursor-pointer flex items-center justify-center w-5 h-5"
                onClick={handleActionClick}
              >
                <Tooltip
                  title={actionTooltip ?? (onClose ? "Close" : "Clear search")}
                >
                  <span className="flex items-center justify-center">
                    {actionIcon ?? (
                      <CloseIcon
                        sx={{
                          fontSize: 16,
                          color: "var(--color-fg-muted)",
                        }}
                      />
                    )}
                  </span>
                </Tooltip>
              </div>
            )}
            {showSubmitButton && (
              <button
                type="button"
                onClick={onSubmit}
                disabled={disabled || isLoading}
                className={`w-5 h-5 flex items-center justify-center text-fg-muted hover:text-fg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  submitVisibility === "onInput"
                    ? "opacity-0 group-hover/searchbar:opacity-100 transition-opacity"
                    : ""
                }`}
              >
                {isLoading ? (
                  <CircularProgress
                    size={14}
                    sx={{ color: "var(--color-accent)" }}
                  />
                ) : (
                  <KeyboardArrowRightOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: "var(--color-fg-muted)",
                    }}
                  />
                )}
              </button>
            )}
          </div>
        )}
      </div>
      {error && errorDisplay === "text" && (
        <div className="mt-1 text-error text-xs truncate">{error}</div>
      )}
    </div>
  );
};
